import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  X,
  Maximize,
  CheckSquare,
  Search,
  Trash2,
  Plus,
  ChevronsLeft,
  ChevronsRight,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  ListChecks,
  Calendar,
  Code
} from "lucide-react";

const SLASH_COMMANDS = [
  {
    id: "heading1",
    label: "Heading 1",
    description: "Insert a level 1 heading",
    keywords: ["heading", "header", "h1"],
    icon: Heading1,
    perform: (ctx) => replaceCommandRange(ctx, "# ")
  },
  {
    id: "heading2",
    label: "Heading 2",
    description: "Insert a level 2 heading",
    keywords: ["heading", "header", "h2"],
    icon: Heading2,
    perform: (ctx) => replaceCommandRange(ctx, "## ")
  },
  {
    id: "heading3",
    label: "Heading 3",
    description: "Insert a level 3 heading",
    keywords: ["heading", "header", "h3"],
    icon: Heading3,
    perform: (ctx) => replaceCommandRange(ctx, "### ")
  },
  {
    id: "bulleted-list",
    label: "Bulleted List",
    description: "Start a bulleted list",
    keywords: ["list", "bullet", "unordered"],
    icon: List,
    perform: (ctx) => replaceCommandRange(ctx, "- ")
  },
  {
    id: "numbered-list",
    label: "Numbered List",
    description: "Start a numbered list",
    keywords: ["list", "numbered", "ordered"],
    icon: ListOrdered,
    perform: (ctx) => replaceCommandRange(ctx, "1. ")
  },
  {
    id: "checklist",
    label: "Checklist",
    description: "Insert a to-do item",
    keywords: ["todo", "task", "check"],
    icon: ListChecks,
    perform: (ctx) => replaceCommandRange(ctx, "- [ ] ")
  },
  {
    id: "insert-date",
    label: "Insert Date",
    description: "Drop in the current date",
    keywords: ["date", "time", "today"],
    icon: Calendar,
    perform: (ctx) =>
      replaceCommandRange(
        ctx,
        `${new Date().toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric"
        })} `
      )
  },
  {
    id: "code-block",
    label: "Code Block",
    description: "Insert a fenced code block",
    keywords: ["code", "block", "snippet"],
    icon: Code,
    perform: (ctx) => {
      const insertion = "\n```\n\n```\n";
      const before = ctx.text.slice(0, ctx.start);
      const after = ctx.text.slice(ctx.end);
      const newText = before + insertion + after;
      const cursor = before.length + insertion.length - 4;
      return { text: newText, cursor };
    }
  }
];

function replaceCommandRange(ctx, insertion, caretOffset = insertion.length) {
  const before = ctx.text.slice(0, ctx.start);
  const after = ctx.text.slice(ctx.end);
  const newText = before + insertion + after;
  const cursor = before.length + caretOffset;
  return { text: newText, cursor };
}

const NOTES_STORAGE_KEY = "tufftimer_notes";
const EDITOR_FONT_SIZE_PX = 17;
const EDITOR_LINE_HEIGHT_PX = 29;
const CODE_FONT_STACK =
  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace";

const MARKDOWN_LINE_STYLE = Object.freeze({
  paragraph: {},
  h1: {
    fontSize: 25,
    fontWeight: 700,
    color: "#111827",
    letterSpacing: 0.1
  },
  h2: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1F2937"
  },
  h3: {
    fontSize: 19,
    fontWeight: 650,
    color: "#374151"
  },
  bulleted: {
    fontWeight: 550,
    color: "#1F2937"
  },
  numbered: {
    fontWeight: 550,
    color: "#1F2937"
  },
  checklist: {
    fontWeight: 550,
    color: "#1F2937"
  },
  codeFence: {
    fontFamily: CODE_FONT_STACK,
    color: "#6B7280"
  },
  code: {
    fontFamily: CODE_FONT_STACK,
    color: "#111827"
  }
});

function getMarkdownLineVariant(line, inCodeBlock) {
  const trimmedStart = line.trimStart();
  const isFence = /^```/.test(trimmedStart);

  if (isFence) {
    return {
      variant: "codeFence",
      nextInCodeBlock: !inCodeBlock
    };
  }

  if (inCodeBlock) {
    return {
      variant: "code",
      nextInCodeBlock: inCodeBlock
    };
  }

  if (/^###\s+/.test(trimmedStart)) {
    return { variant: "h3", nextInCodeBlock: inCodeBlock };
  }
  if (/^##\s+/.test(trimmedStart)) {
    return { variant: "h2", nextInCodeBlock: inCodeBlock };
  }
  if (/^#\s+/.test(trimmedStart)) {
    return { variant: "h1", nextInCodeBlock: inCodeBlock };
  }
  if (/^-\s+\[[ xX]\]\s+/.test(trimmedStart)) {
    return { variant: "checklist", nextInCodeBlock: inCodeBlock };
  }
  if (/^-\s+/.test(trimmedStart)) {
    return { variant: "bulleted", nextInCodeBlock: inCodeBlock };
  }
  if (/^\d+\.\s+/.test(trimmedStart)) {
    return { variant: "numbered", nextInCodeBlock: inCodeBlock };
  }

  return {
    variant: "paragraph",
    nextInCodeBlock: inCodeBlock
  };
}

function buildMarkdownStyledLines(markdown) {
  const lines = markdown.split("\n");
  const parsed = [];
  let inCodeBlock = false;

  for (const line of lines) {
    const { variant, nextInCodeBlock } = getMarkdownLineVariant(line, inCodeBlock);
    parsed.push({ text: line, variant });
    inCodeBlock = nextInCodeBlock;
  }

  return parsed;
}

function createEmptyNote(overrides = {}) {
  const now = Date.now();
  const id =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${now}-${Math.random().toString(36).slice(2, 9)}`;
  return {
    id,
    title: "",
    content: "",
    created: now,
    updated: now,
    checklist: false,
    autoTitle: true,
    ...overrides
  };
}

function getInitialNotes() {
  try {
    const saved = localStorage.getItem(NOTES_STORAGE_KEY);
    if (!saved) return [];

    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((note) => note && typeof note === "object")
      .map((note) => createEmptyNote({
        id:
          typeof note.id === "string" && note.id.length > 0
            ? note.id
            : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        title: typeof note.title === "string" ? note.title : "",
        content: typeof note.content === "string" ? note.content : "",
        created: typeof note.created === "number" ? note.created : Date.now(),
        updated: typeof note.updated === "number" ? note.updated : Date.now(),
        checklist: !!note.checklist,
        autoTitle: note.autoTitle !== false
      }));
  } catch {
    return [];
  }
}

// Style constants extracted outside component to avoid recreation
const STYLE_CONSTANTS = {
  appleBg: "#F6F6F6",
  appleSidebar: "#FAFAFA",
  appleBorder: "#E5E5E7",
  appleYellow: "#FFE066",
  appleText: "#222",
  appleNoteSelected: "#FFFBEA",
  appleShadow: "0 8px 32px 0 rgba(60,60,60,0.12)",
  appleFont: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`
};

// Resizable hook extracted outside component
function useResizable(ref, setSize) {
  const minW = 300, minH = 200;

  const onMouseDown = useCallback((e, handle) => {
    e.preventDefault();
    const modal = ref.current;
    if (!modal) return;

    const startX = e.clientX, startY = e.clientY;
    const rect = modal.getBoundingClientRect();
    const startW = rect.width, startH = rect.height;
    const origLeft = rect.left, origTop = rect.top;

    function onMouseMove(e) {
      const dx = e.clientX - startX, dy = e.clientY - startY;
      let newW = startW, newH = startH, newLeft = origLeft, newTop = origTop;

      if (["right", "corner-br", "corner-tr"].includes(handle)) newW = Math.max(minW, startW + dx);
      if (["left", "corner-bl", "corner-tl"].includes(handle)) { newW = Math.max(minW, startW - dx); newLeft = origLeft + dx; }
      if (["bottom", "corner-br", "corner-bl"].includes(handle)) newH = Math.max(minH, startH + dy);
      if (["top", "corner-tr", "corner-tl"].includes(handle)) { newH = Math.max(minH, startH - dy); newTop = origTop + dy; }

      newW = Math.min(newW, window.innerWidth - newLeft);
      newH = Math.min(newH, window.innerHeight - newTop);
      newLeft = Math.min(Math.max(0, newLeft), window.innerWidth - newW);
      newTop = Math.min(Math.max(0, newTop), window.innerHeight - newH);

      modal.style.left = `${newLeft}px`;
      modal.style.top = `${newTop}px`;
      setSize({ width: newW, height: newH });
    }

    function onMouseUp() {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    }

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, [ref, setSize]);

  return { onMouseDown };
}

export default function NotepadModal({ show, onClose }) {

  const [notes, setNotes] = useState(() => getInitialNotes());
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [checklistMode, setChecklistMode] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [editingContent, setEditingContent] = useState("");
  const [editingTitle, setEditingTitle] = useState("");
  const [autoTitle, setAutoTitle] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const modalRef = useRef(null);
  const contentRef = useRef(null);
  const markdownPreviewInnerRef = useRef(null);
  const contentAreaRef = useRef(null);
  const dragData = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    origX: 0,
    origY: 0
  });
  const [modalSize, setModalSize] = useState({ width: 450, height: 300 });
  const [commandMenuVisible, setCommandMenuVisible] = useState(false);
  const [commandQuery, setCommandQuery] = useState("");
  const [commandStart, setCommandStart] = useState(null);
  const [commandPosition, setCommandPosition] = useState({ top: 0, left: 0 });
  const [highlightedCommand, setHighlightedCommand] = useState(0);
  const selectedNote = useMemo(
    () => notes.find((note) => note.id === selectedId) || null,
    [notes, selectedId]
  );

  // ─── Computed (memoized) ──────
  const searchLower = search.toLowerCase();
  const filteredNotes = useMemo(() =>
    notes.filter(n =>
      n.title.toLowerCase().includes(searchLower) ||
      n.content.toLowerCase().includes(searchLower)
    ), [notes, searchLower]);

  const filteredCommands = useMemo(() => {
    if (!commandMenuVisible) return [];
    const query = commandQuery.trim().toLowerCase();
    if (!query) return SLASH_COMMANDS;
    return SLASH_COMMANDS.filter(cmd =>
      [cmd.label, cmd.description, ...(cmd.keywords || [])]
        .join(" ").toLowerCase().includes(query)
    );
  }, [commandQuery, commandMenuVisible]);
  const markdownStyledLines = useMemo(
    () => buildMarkdownStyledLines(editingContent || ""),
    [editingContent]
  );

  // ─── Effects (load & sync notes) ───────────────────────
  useEffect(() => {
    try {
      localStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(notes));
    } catch {
      // Ignore storage errors so the editor remains usable.
    }
  }, [notes]);

  useEffect(() => {
    if (!notes.length) {
      if (selectedId !== null) {
        setSelectedId(null);
      }
      return;
    }

    const selectedExists = notes.some((note) => note.id === selectedId);
    if (!selectedExists) {
      setSelectedId(notes[0].id);
    }
  }, [notes, selectedId]);

  useEffect(() => {
    setEditingTitle(selectedNote?.title || "");
    setEditingContent(selectedNote?.content || "");
    setChecklistMode(!!selectedNote?.checklist);
    setAutoTitle(selectedNote ? selectedNote.autoTitle !== false : true);
  }, [selectedNote]);


  // ─── Handlers (memoized) ─────
  const closeCommandMenu = useCallback(() => {
    setCommandMenuVisible(false);
    setCommandQuery("");
    setCommandStart(null);
    setHighlightedCommand(0);
  }, []);

  // ─── Consolidated command menu effects ─────
  useEffect(() => {
    if (!commandMenuVisible) return;

    function handleOutsideClick(e) {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        closeCommandMenu();
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [commandMenuVisible, closeCommandMenu]);

  useEffect(() => {
    // Close command menu when switching to checklist mode or closing modal
    if ((checklistMode || !show) && commandMenuVisible) {
      closeCommandMenu();
      return;
    }
    if (!commandMenuVisible) return;

    // Reset highlighted index when query changes
    setHighlightedCommand(0);
  }, [commandQuery, commandMenuVisible, checklistMode, show, closeCommandMenu]);

  // Clamp highlighted index when filtered results change
  useEffect(() => {
    if (!commandMenuVisible || !filteredCommands.length) return;
    setHighlightedCommand(idx => Math.min(idx, filteredCommands.length - 1));
  }, [filteredCommands.length, commandMenuVisible]);


  function updateSlashCommandState(value, caretIndex) {
    if (checklistMode || !contentRef.current) return;
    const beforeCaret = value.slice(0, caretIndex ?? value.length);
    const slashIdx = beforeCaret.lastIndexOf("/");
    if (slashIdx === -1) {
      if (commandMenuVisible) closeCommandMenu();
      return;
    }
    const charBeforeSlash = beforeCaret[slashIdx - 1];
    const isValidPrefix = slashIdx === 0 || /\s/.test(charBeforeSlash || "");
    const query = beforeCaret.slice(slashIdx + 1);
    const hasWhitespace = /\s/.test(query);
    if (!isValidPrefix || hasWhitespace) {
      if (commandMenuVisible) closeCommandMenu();
      return;
    }

    setCommandStart(slashIdx);
    setCommandQuery(query);
    if (!commandMenuVisible) {
      setCommandMenuVisible(true);
    }

    if (contentAreaRef.current) {
      const areaRect = contentAreaRef.current.getBoundingClientRect();
      const textareaRect = contentRef.current.getBoundingClientRect();
      const menuWidth = 280;
      const menuHeight = 260;
      const relativeTop = textareaRect.top - areaRect.top + 40;
      const relativeLeft = textareaRect.left - areaRect.left + 8;
      const top = Math.max(0, Math.min(relativeTop, areaRect.height - menuHeight));
      const left = Math.max(0, Math.min(relativeLeft, areaRect.width - menuWidth));
      setCommandPosition((prev) =>
        prev.top === top && prev.left === left ? prev : { top, left }
      );
    } else {
      setCommandPosition((prev) =>
        prev.top === 32 && prev.left === 24 ? prev : { top: 32, left: 24 }
      );
    }
  }

  function executeSlashCommand(command) {
    if (commandStart === null || !contentRef.current) return;
    const textarea = contentRef.current;
    const selectionEnd = textarea.selectionStart;
    if (selectionEnd < commandStart) {
      closeCommandMenu();
      return;
    }
    const currentValue = textarea.value;
    const context = {
      text: currentValue,
      start: commandStart,
      end: selectionEnd
    };
    const result = command.perform(context);
    if (!result) return;
    ensureActiveNote();
    setEditingContent(result.text);
    if (autoTitle) {
      const [firstLine] = result.text.split("\n");
      setEditingTitle(firstLine);
    }
    closeCommandMenu();
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(result.cursor, result.cursor);
    });
  }

  function handleTextareaKeyDown(e) {
    if (!commandMenuVisible) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedCommand((idx) =>
        filteredCommands.length
          ? (idx + 1) % filteredCommands.length
          : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedCommand((idx) =>
        filteredCommands.length
          ? (idx - 1 + filteredCommands.length) % filteredCommands.length
          : 0
      );
    } else if (e.key === "Enter") {
      const target = filteredCommands[highlightedCommand];
      if (target) {
        e.preventDefault();
        executeSlashCommand(target);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeCommandMenu();
    }
  }

  function handleTextareaSelect(e) {
    if (!commandMenuVisible) return;
    updateSlashCommandState(e.target.value, e.target.selectionStart);
  }

  const syncMarkdownPreviewScroll = useCallback(() => {
    const textarea = contentRef.current;
    const previewInner = markdownPreviewInnerRef.current;
    if (!textarea || !previewInner) return;

    previewInner.style.transform = `translate(${-textarea.scrollLeft}px, ${-textarea.scrollTop}px)`;
  }, []);

  // --- Auto-create note on first keystroke ---
  function ensureActiveNote() {
    if (selectedId) return selectedId;
    const newNote = createEmptyNote();

    // Create & select immediately (also persists via the notes useEffect)
    setNotes((ns) => [newNote, ...ns]);
    setSelectedId(newNote.id);
    return newNote.id;
  }

  function handleNewNote() {
    const newNote = createEmptyNote();
    setNotes((prevNotes) => [newNote, ...prevNotes]);
    setSelectedId(newNote.id);
  }

  function handleDeleteNote(id) {
    const idx = notes.findIndex((note) => note.id === id);
    if (idx === -1) return;

    const nextNotes = notes.filter((note) => note.id !== id);
    setNotes(nextNotes);
    setSelectedId((currentSelectedId) => {
      if (currentSelectedId !== id) return currentSelectedId;
      if (!nextNotes.length) return null;
      return nextNotes[Math.max(0, idx - 1)].id;
    });
  }

  function handleSave() {
    const hasDraftContent =
      editingTitle.trim().length > 0 || editingContent.trim().length > 0;
    if (!selectedId && !hasDraftContent) return;

    const noteId = selectedId || ensureActiveNote();
    if (!noteId) return;

    const nextTitle = editingTitle || "Untitled";
    setNotes((prevNotes) => {
      let changed = false;
      const nextNotes = prevNotes.map((note) => {
        if (note.id !== noteId) return note;
        if (
          note.title === nextTitle &&
          note.content === editingContent &&
          note.checklist === checklistMode &&
          note.autoTitle === autoTitle
        ) {
          return note;
        }

        changed = true;
        return {
          ...note,
          title: nextTitle,
          content: editingContent,
          updated: Date.now(),
          checklist: checklistMode,
          autoTitle
        };
      });

      return changed ? nextNotes : prevNotes;
    });
  }

  function handleChecklistToggle() {
    const nextMode = !checklistMode;
    setChecklistMode(nextMode);

    if (!selectedId) return;

    setNotes((prevNotes) => {
      let changed = false;
      const nextNotes = prevNotes.map((note) => {
        if (note.id !== selectedId || note.checklist === nextMode) {
          return note;
        }
        changed = true;
        return { ...note, checklist: nextMode };
      });
      return changed ? nextNotes : prevNotes;
    });
  }

  // ─── Dragging ────────
  const onDrag = useCallback((e) => {
    if (!dragData.current.isDragging || !modalRef.current) return;
    const dx = e.clientX - dragData.current.startX;
    const dy = e.clientY - dragData.current.startY;
    let newLeft = dragData.current.origX + dx;
    let newTop = dragData.current.origY + dy;

    const modal = modalRef.current;
    const { offsetWidth: w, offsetHeight: h } = modal;
    newLeft = Math.min(Math.max(0, newLeft), window.innerWidth - w);
    newTop = Math.min(Math.max(0, newTop), window.innerHeight - h);
    modal.style.left = `${newLeft}px`;
    modal.style.top = `${newTop}px`;
  }, []);

  const endDrag = useCallback(function endDragHandler() {
    dragData.current.isDragging = false;
    document.removeEventListener("mousemove", onDrag);
    document.removeEventListener("mouseup", endDragHandler);
  }, [onDrag]);

  const startDrag = useCallback((e) => {
    if (fullscreen || e.button !== 0) return;
    if (
      e.target.closest(
        "button, input, textarea, select, [data-no-drag='true']"
      )
    ) {
      return;
    }

    const modal = modalRef.current;
    if (!modal) return;

    e.preventDefault();
    const rect = modal.getBoundingClientRect();
    dragData.current = {
      isDragging: true,
      startX: e.clientX,
      startY: e.clientY,
      origX: rect.left,
      origY: rect.top
    };
    document.addEventListener("mousemove", onDrag);
    document.addEventListener("mouseup", endDrag);
  }, [fullscreen, onDrag, endDrag]);

  useEffect(() => {
    return () => {
      dragData.current.isDragging = false;
      document.removeEventListener("mousemove", onDrag);
      document.removeEventListener("mouseup", endDrag);
    };
  }, [onDrag, endDrag]);

  useEffect(() => {
    if (show) return;
    dragData.current.isDragging = false;
    document.removeEventListener("mousemove", onDrag);
    document.removeEventListener("mouseup", endDrag);
  }, [show, onDrag, endDrag]);

  useEffect(() => {
    if (!show || checklistMode) return;
    syncMarkdownPreviewScroll();
  }, [show, checklistMode, editingContent, syncMarkdownPreviewScroll]);

  // Use the extracted resizable hook
  const { onMouseDown: onResizeMouseDown } = useResizable(modalRef, setModalSize);

  if (!show) return null;

  // ─── Styles & dimensions (using extracted constants) ─────
  const sidebarWidth = sidebarCollapsed ? 48 : 270;
  const { appleBg, appleSidebar, appleBorder, appleYellow, appleText, appleNoteSelected, appleShadow, appleFont } = STYLE_CONSTANTS;

  return (
    <div
      ref={modalRef}
      className={`relative flex shadow-lg ${fullscreen ? "w-screen h-screen max-w-none z-[9999] rounded-none" : ""
        }`}
      style={{
        position: "fixed",
        top: fullscreen ? 0 : 80,
        left: fullscreen ? 0 : 20,
        width: fullscreen ? "100vw" : modalSize.width,
        height: fullscreen ? "100vh" : modalSize.height,
        background: appleBg,
        color: appleText,
        fontFamily: appleFont,
        boxShadow: appleShadow,
        border: `1.5px solid ${appleBorder}`,
        borderRadius: fullscreen ? 0 : 16,
        transition: "width 0.1s, height 0.1s",
        zIndex: 2000
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Resize handles */}
      {[
        ["right", "absolute right-0 top-0 h-full w-2 cursor-ew-resize z-50"],
        ["left", "absolute left-0 top-0 h-full w-2 cursor-ew-resize z-50"],
        [
          "bottom",
          "absolute bottom-0 left-0 w-full h-2 cursor-ns-resize z-50"
        ],
        ["top", "absolute top-0 left-0 w-full h-2 cursor-ns-resize z-50"],
        [
          "corner-br",
          "absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize z-50"
        ],
        [
          "corner-bl",
          "absolute bottom-0 left-0 w-4 h-4 cursor-nesw-resize z-50"
        ],
        [
          "corner-tr",
          "absolute top-0 right-0 w-4 h-4 cursor-nesw-resize z-50"
        ],
        [
          "corner-tl",
          "absolute top-0 left-0 w-4 h-4 cursor-nwse-resize z-50"
        ]
      ].map(([h, cls]) => (
        <div
          key={h}
          onMouseDown={(e) => onResizeMouseDown(e, h)}
          className={cls}
        />
      ))}

      {/* Sidebar */}
      <div
        className="flex flex-col h-full"
        style={{
          width: sidebarWidth,
          background: appleSidebar,
          borderRight: `1.5px solid ${appleBorder}`,
          borderTopLeftRadius: fullscreen ? 0 : 16,
          borderBottomLeftRadius: fullscreen ? 0 : 16,
          overflow: "hidden",
          transition: "width 0.2s"
        }}
      >
        {/* New + Collapse */}
        <div className="px-2 pt-4 pb-2 flex items-center justify-end space-x-2">
          {!sidebarCollapsed && (
            <button
              onClick={handleNewNote}
              title="New Note"
              className="rounded p-1 hover:bg-yellow-100"
              style={{ color: appleText }}
            >
              <Plus size={20} />
            </button>
          )}
          <button
            onClick={() => setSidebarCollapsed((c) => !c)}
            title={sidebarCollapsed ? "Expand" : "Collapse"}
            className="p-1 hover:bg-gray-200 rounded"
          >
            {sidebarCollapsed ? (
              <ChevronsRight size={20} />
            ) : (
              <ChevronsLeft size={20} />
            )}
          </button>
        </div>

        {/* Search */}
        {!sidebarCollapsed && (
          <div className="px-4 pb-3">
            <div className="flex items-center bg-white rounded-lg border border-gray-200 px-2 py-1">
              <Search size={16} className="text-zinc-400" />
              <input
                className="flex-1 bg-transparent outline-none px-2 text-base"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ fontFamily: appleFont }}
              />
            </div>
          </div>
        )}

        {/* Note list */}
        <div className="flex-1 overflow-y-auto">
          {!sidebarCollapsed &&
            filteredNotes.map((note) => (
              <div
                key={note.id}
                onClick={() => setSelectedId(note.id)}
                className={`px-5 py-3 border-b last:border-b-0 cursor-pointer transition ${selectedId === note.id ? "" : "hover:bg-gray-100"
                  }`}
                style={{
                  background:
                    selectedId === note.id ? appleNoteSelected : "transparent",
                  borderLeft:
                    selectedId === note.id
                      ? `4px solid ${appleYellow}`
                      : "4px solid transparent",
                  borderRadius: selectedId === note.id ? 8 : 0,
                  margin: selectedId === note.id ? "4px 0" : 0,
                  boxShadow:
                    selectedId === note.id
                      ? "0 2px 8px 0 rgba(255,224,102,0.08)"
                      : "none",
                  borderColor: appleBorder
                }}
              >
                <div className="flex items-center justify-between">
                  <span
                    className="font-semibold text-base truncate"
                    style={{ maxWidth: 140 }}
                  >

                    {(selectedId === note.id ? editingTitle : note.title) || "Untitled"}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteNote(note.id);
                    }}
                    title="Delete"
                    className="ml-2 p-1 rounded hover:bg-zinc-200"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <span
                  className="text-xs text-zinc-400 mt-1"
                  style={{ fontSize: 12 }}
                >
                  {new Date(note.updated).toLocaleString()}
                </span>
              </div>
            ))}
        </div>
      </div>

      {/* Main Panel */}
      <div
        className="flex-1 flex flex-col h-full"
        style={{
          background: "#fff",
          borderTopRightRadius: fullscreen ? 0 : 16,
          borderBottomRightRadius: fullscreen ? 0 : 16
        }}
      >
        {/* Header (drag handle) */}
        <div
          className="draggable-handle flex items-center justify-between px-8 py-5 border-b"
          style={{
            borderColor: appleBorder,
            background: "#fff",
            cursor: fullscreen ? "default" : "grab"
          }}
          onMouseDown={startDrag}
        >
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              title="Close"
              className="p-1 rounded hover:bg-zinc-200"
              style={{ color: appleText }}
            >
              <X size={20} />
            </button>
            <button
              onClick={() => setFullscreen((f) => !f)}
              title="Fullscreen"
              className="p-1 rounded hover:bg-zinc-200"
              style={{ color: appleText }}
            >
              <Maximize size={20} />
            </button>
            <button
              onClick={handleChecklistToggle}
              title="Checklist Mode"
              className={`p-1 rounded hover:bg-yellow-100 ${checklistMode ? "bg-yellow-200" : ""
                }`}
              style={{ color: appleText }}
            >
              <CheckSquare size={20} />
            </button>
          </div>
          <input
            className="font-bold text-2xl bg-transparent outline-none flex-1 px-2"
            value={editingTitle}
            onChange={(e) => {
              ensureActiveNote();
              setAutoTitle(false);
              setEditingTitle(e.target.value);
            }}
            onBlur={handleSave}
            placeholder="Title"
            style={{
              minWidth: 0,
              fontFamily: appleFont,
              color: appleText,
              letterSpacing: 0.1
            }}
            maxLength={60}
          />
        </div>

        {/* Content */}
        <div
          ref={contentAreaRef}
          className="flex-1 overflow-y-auto px-10 py-8 relative"
        >
          {checklistMode ? (
            <ChecklistEditor
              value={editingContent}
              onChange={(val) => {
                setEditingContent(val);
                if (autoTitle) {
                  const [firstLine] = val.split("\n");
                  setEditingTitle(firstLine);
                }
              }}
              onBlur={handleSave}
            />
          ) : (
            <div className="relative w-full h-full min-h-[300px]">
              <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none overflow-hidden"
                style={{
                  fontFamily: appleFont,
                  color: appleText,
                  fontSize: `${EDITOR_FONT_SIZE_PX}px`,
                  lineHeight: `${EDITOR_LINE_HEIGHT_PX}px`,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  userSelect: "none"
                }}
              >
                <div
                  ref={markdownPreviewInnerRef}
                  style={{
                    willChange: "transform"
                  }}
                >
                  {editingContent ? (
                    markdownStyledLines.map((line, idx) => (
                      <div
                        key={`md-line-${idx}`}
                        style={{
                          minHeight: `${EDITOR_LINE_HEIGHT_PX}px`,
                          lineHeight: `${EDITOR_LINE_HEIGHT_PX}px`,
                          ...MARKDOWN_LINE_STYLE[line.variant]
                        }}
                      >
                        {line.text || "\u200B"}
                      </div>
                    ))
                  ) : (
                    <div
                      style={{
                        minHeight: `${EDITOR_LINE_HEIGHT_PX}px`,
                        lineHeight: `${EDITOR_LINE_HEIGHT_PX}px`,
                        color: "#9CA3AF"
                      }}
                    >
                      Start typing...
                    </div>
                  )}
                </div>
              </div>
              <textarea
                ref={contentRef}
                className="relative z-10 w-full h-full min-h-[300px] bg-transparent outline-none resize-none text-base"
                value={editingContent}
                onChange={(e) => {
                  ensureActiveNote();
                  const text = e.target.value;
                  setEditingContent(text);
                  if (autoTitle) {
                    const [firstLine] = text.split("\n");
                    setEditingTitle(firstLine);
                  }
                  updateSlashCommandState(text, e.target.selectionStart);
                }}
                onKeyDown={handleTextareaKeyDown}
                onSelect={handleTextareaSelect}
                onScroll={syncMarkdownPreviewScroll}
                onBlur={handleSave}
                style={{
                  fontFamily: appleFont,
                  color: "transparent",
                  WebkitTextFillColor: "transparent",
                  caretColor: appleText,
                  fontSize: `${EDITOR_FONT_SIZE_PX}px`,
                  lineHeight: `${EDITOR_LINE_HEIGHT_PX}px`,
                  padding: 0,
                  border: "none",
                  background: "transparent",
                  boxShadow: "none",
                  outline: "none",
                  resize: "none"
                }}
              />
            </div>
          )}
          {commandMenuVisible && !checklistMode && (
            <div
              className="absolute z-50 w-64 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
              style={{
                top: commandPosition.top,
                left: commandPosition.left
              }}
            >
              <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-400 border-b border-gray-200">
                Commands
              </div>
              <div className="max-h-64 overflow-y-auto">
                {filteredCommands.length ? (
                  filteredCommands.map((cmd, idx) => {
                    const Icon = cmd.icon;
                    return (
                      <button
                        type="button"
                        key={cmd.id}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => executeSlashCommand(cmd)}
                        onMouseEnter={() => setHighlightedCommand(idx)}
                        className={`w-full flex items-start gap-3 px-4 py-3 text-left transition ${highlightedCommand === idx
                          ? "bg-yellow-100"
                          : "hover:bg-gray-50"
                          }`}
                      >
                        <div className="mt-1 text-gray-400">
                          <Icon size={18} />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-sm text-gray-900">
                            {cmd.label}
                          </div>
                          <div className="text-xs text-gray-500">
                            {cmd.description}
                          </div>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="px-4 py-3 text-sm text-gray-400">
                    No commands match "{commandQuery}".
                  </div>
                )}
              </div>
              <div className="px-4 py-2 text-[11px] text-gray-400 border-t border-gray-200 flex items-center justify-between">
                <span>Type "/" to search</span>
                <span>esc</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChecklistEditor({ value, onChange, onBlur }) {
  const [items, setItems] = useState(() => (value ? value.split("\n") : [""]));
  const lastValueRef = useRef(value);

  // Only sync from parent when value actually changes from outside
  useEffect(() => {
    if (value !== lastValueRef.current) {
      setItems(value ? value.split("\n") : [""]);
      lastValueRef.current = value;
    }
  }, [value]);

  const toggleCheck = useCallback((idx) => {
    setItems(prev => {
      const ni = [...prev];
      ni[idx] = ni[idx].startsWith("[x] ")
        ? ni[idx].replace(/^\[x\] /, "[ ] ")
        : "[x] " + ni[idx];
      const newValue = ni.join("\n");
      lastValueRef.current = newValue;
      onChange(newValue);
      return ni;
    });
  }, [onChange]);

  const updateText = useCallback((idx, text, isChecked) => {
    setItems(prev => {
      const ni = [...prev];
      const prefix = isChecked ? "[x] " : "[ ] ";
      ni[idx] = prefix + text;
      const newValue = ni.join("\n");
      lastValueRef.current = newValue;
      onChange(newValue);
      return ni;
    });
  }, [onChange]);

  const addItem = useCallback((idx) => {
    setItems(prev => {
      const ni = [...prev];
      ni.splice(idx + 1, 0, "");
      const newValue = ni.join("\n");
      lastValueRef.current = newValue;
      onChange(newValue);
      return ni;
    });
  }, [onChange]);

  const deleteItem = useCallback((idx) => {
    setItems(prev => {
      if (prev.length <= 1) return prev;
      const ni = prev.filter((_, i) => i !== idx);
      const newValue = ni.join("\n");
      lastValueRef.current = newValue;
      onChange(newValue);
      return ni;
    });
  }, [onChange]);

  return (
    <div className="flex flex-col gap-2">
      {items.map((item, idx) => {
        const isChecked = item.startsWith("[x] ");
        const displayText = item.replace(/^\[.\] /, "");
        return (
          <div key={idx} className="flex items-center gap-2 group">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() => toggleCheck(idx)}
              className="accent-yellow-500 w-5 h-5"
            />
            <input
              className="flex-1 bg-transparent outline-none text-base px-1"
              value={displayText}
              onChange={(e) => updateText(idx, e.target.value, isChecked)}
              onBlur={onBlur}
              placeholder="To-do item"
              maxLength={120}
            />
            <button
              onClick={() => addItem(idx)}
              title="Add item"
              className="opacity-0 group-hover:opacity-100 transition p-1"
            >
              <Plus size={16} />
            </button>
            <button
              onClick={() => deleteItem(idx)}
              title="Delete item"
              className="opacity-0 group-hover:opacity-100 transition p-1"
              disabled={items.length === 1}
            >
              <Trash2 size={16} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
